import { useEffect, useState } from "react";
import {
  TrendingUp,
  Wallet,
  Target,
  Activity,
  Calendar,
  Trophy,
} from "lucide-react";
import { analyticsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatFCFA, statutLabel } from "@/lib/crmHelpers";

const KpiCard = ({ icon: Icon, label, value, sub, accent = "navy", testId }) => {
  const accentClass =
    accent === "gold"
      ? "text-[hsl(var(--accent-gold))] bg-amber-50 dark:bg-amber-950/40"
      : accent === "emerald"
      ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40"
      : "text-[hsl(var(--accent-navy))] bg-blue-50 dark:bg-blue-950/40";
  return (
    <div
      className="rounded-xl border border-border/60 bg-card p-4"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-label text-muted-foreground">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${accentClass}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 font-heading text-2xl font-semibold tracking-tight">
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
};

const FunnelBar = ({ stage, reached, rate, isLast }) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between text-xs">
      <span className="font-medium capitalize">{statutLabel(stage)}</span>
      <span className="text-muted-foreground">
        {reached} · {rate}%
      </span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-muted">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          isLast
            ? "bg-emerald-500"
            : "bg-[hsl(var(--accent-navy))]"
        }`}
        style={{ width: `${Math.max(2, rate)}%` }}
      />
    </div>
  </div>
);

export default function SalesKPIsSection() {
  const { activeProjectId } = useSettings();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    analyticsApi
      .salesKpis(activeProjectId)
      .then(setKpis)
      .catch(() => {});
  }, [activeProjectId]);

  if (!kpis) return null;

  return (
    <section data-testid="sales-kpis-section">
      <div className="mb-4">
        <div className="text-label text-muted-foreground">Performance commerciale</div>
        <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <TrendingUp className="h-6 w-6 text-[hsl(var(--accent-navy))]" />
          Pipeline & Prévisions de revenus
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
        <KpiCard
          icon={Wallet}
          label="Pipeline pondéré"
          value={formatFCFA(kpis.weighted_pipeline)}
          sub={`Brut : ${formatFCFA(kpis.raw_pipeline)}`}
          accent="navy"
          testId="kpi-weighted-pipeline"
        />
        <KpiCard
          icon={Calendar}
          label="Prévision 30j"
          value={formatFCFA(kpis.forecast_30d)}
          sub="Closing dans 30 jours"
          accent="emerald"
          testId="kpi-forecast-30d"
        />
        <KpiCard
          icon={Calendar}
          label="Prévision 60j"
          value={formatFCFA(kpis.forecast_60d)}
          sub="Closing 31-60j"
          accent="navy"
          testId="kpi-forecast-60d"
        />
        <KpiCard
          icon={Calendar}
          label="Prévision 90j"
          value={formatFCFA(kpis.forecast_90d)}
          sub="Closing 61-90j"
          accent="navy"
          testId="kpi-forecast-90d"
        />
        <KpiCard
          icon={Activity}
          label="Vélocité moyenne"
          value={kpis.velocity_days ? `${kpis.velocity_days}j` : "—"}
          sub="Jours pour gagner"
          accent="gold"
          testId="kpi-velocity"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="rounded-xl border border-border/60 bg-card p-5">
          <div className="mb-4 flex items-center gap-2">
            <Target className="h-4 w-4 text-[hsl(var(--accent-navy))]" />
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
              Entonnoir de conversion
            </h3>
          </div>
          <div className="space-y-3" data-testid="conversion-funnel">
            {kpis.conversion_funnel.map((s, i) => (
              <FunnelBar
                key={s.stage}
                stage={s.stage}
                reached={s.reached}
                rate={s.rate}
                isLast={i === kpis.conversion_funnel.length - 1}
              />
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border/60 bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-emerald-600" />
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
              Bilan
            </h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Leads actifs</span>
              <span className="font-heading font-semibold">{kpis.active_leads}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Gagnés</span>
              <span className="font-heading font-semibold text-emerald-600">
                {kpis.won_count}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Perdus</span>
              <span className="font-heading font-semibold text-zinc-500">
                {kpis.lost_count}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-border/40 pt-3">
              <span className="text-muted-foreground">Win rate</span>
              <span className="font-heading font-semibold">
                {kpis.won_count + kpis.lost_count > 0
                  ? `${Math.round(
                      (kpis.won_count / (kpis.won_count + kpis.lost_count)) * 100
                    )}%`
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
